import { useIntl } from '@umijs/max';
import { App, theme } from 'antd';
import type { ReactNode } from 'react';
import { useCallback } from 'react';

export interface DangerConfirmOptions {
  title: ReactNode;
  description?: ReactNode;
  irreversibleHint?: boolean;
  tone?: 'danger' | 'primary';
  okText?: string;
  cancelText?: string;
  onOk: () => void | Promise<void>;
}

export function useDangerConfirm() {
  const { modal } = App.useApp();
  const intl = useIntl();
  const { token } = theme.useToken();

  return useCallback(
    (opts: DangerConfirmOptions) => {
      const tone = opts.tone ?? 'danger';
      const okLabel =
        opts.okText ??
        intl.formatMessage({
          id:
            tone === 'danger' ? 'drama.common.delete' : 'drama.common.confirm',
          defaultMessage: tone === 'danger' ? '删除' : '确认',
        });
      const cancelLabel =
        opts.cancelText ??
        intl.formatMessage({
          id: 'drama.common.cancel',
          defaultMessage: '取消',
        });
      const irreversibleLabel = intl.formatMessage({
        id: 'drama.common.irreversibleHint',
        defaultMessage: '此操作不可撤销',
      });

      const hasBody = !!opts.description || !!opts.irreversibleHint;

      const confirmation = modal.confirm({
        title: opts.title,
        content: hasBody ? (
          <div
            style={{
              color: token.colorTextSecondary,
              fontSize: token.fontSize,
              lineHeight: token.lineHeight,
              marginTop: token.marginXXS,
            }}
          >
            {opts.description}
            {opts.description && opts.irreversibleHint ? ' ' : null}
            {opts.irreversibleHint && (
              <span style={{ color: token.colorTextTertiary }}>
                {irreversibleLabel}
              </span>
            )}
          </div>
        ) : null,
        icon: null,
        closable: true,
        okText: okLabel,
        cancelText: cancelLabel,
        okButtonProps: { danger: tone === 'danger', type: 'primary' },
        cancelButtonProps: { type: 'text' },
        centered: true,
        width: 400,
        maskClosable: false,
        onOk: opts.onOk,
      });
      // Ant Design's await mode keeps rejected mutations retryable without
      // emitting an unhandled rejection. The public helper still returns void.
      void confirmation.then(() => undefined, () => undefined);
    },
    [modal, intl, token],
  );
}
